import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ContractorPortalApi from "../../../../../infrastructure/api/contractorPortalApi";
import { formatCurrency, formatDate } from "../../../../../infrastructure/utils/formatters";
import PaymentStatusBadge from "../components/PaymentStatusBadge";

const ContractorProjectPaymentsTab = ({ executionId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const res = await ContractorPortalApi.getPayments(executionId);
      if (cancelled) return;
      if (!res?.isSuccess) {
        setError(res?.message || "خطا در بارگذاری پرداخت‌ها.");
        setPayments([]);
      } else {
        setPayments(res.data?.payments || []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [executionId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Stack spacing={2} data-testid="contractor-payments-tab">
      <Typography variant="body2" color="text.secondary">
        مبالغ نمایش‌داده‌شده فقط سهم پیمانکار است و شامل قیمت فروش مشتری یا مارجین
        سولارنت نیست.
      </Typography>

      {!payments.length ? (
        <Alert severity="info" data-testid="payments-empty">
          درخواست پرداختی برای این پروژه ثبت نشده است.
        </Alert>
      ) : (
        <Paper variant="outlined" sx={{ overflowX: "auto" }}>
          <Table size="small" data-testid="payments-table">
            <TableHead>
              <TableRow>
                <TableCell>شناسه</TableCell>
                <TableCell>ناخالص</TableCell>
                <TableCell>کسر تضمین</TableCell>
                <TableCell>بیمه / تأمین اجتماعی</TableCell>
                <TableCell>خالص</TableCell>
                <TableCell>وضعیت</TableCell>
                <TableCell>تاریخ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((p) => (
                <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                  <TableCell>{p.id}</TableCell>
                  <TableCell>
                    {p.grossAmount != null
                      ? formatCurrency(p.grossAmount, "rial")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {p.retentionAmount != null
                      ? formatCurrency(p.retentionAmount, "rial")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {p.insuranceRetentionAmount != null
                      ? formatCurrency(p.insuranceRetentionAmount, "rial")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {p.netPayableAmount != null
                      ? formatCurrency(p.netPayableAmount, "rial")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <PaymentStatusBadge
                      status={p.status}
                      statusTitle={p.statusTitle}
                    />
                  </TableCell>
                  <TableCell>
                    {p.requestedAt ? formatDate(p.requestedAt) : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Privacy guard markers for tests */}
      <Box
        data-testid="payment-privacy-guard"
        sx={{ display: "none" }}
        aria-hidden
      />
    </Stack>
  );
};

export default ContractorProjectPaymentsTab;
